import { useState, useEffect } from 'react'
import { Chrome, X, Download, Play } from 'lucide-react'
import { ExtensionDemoButton } from '../ExtensionDemo'

export default function ExtensionBanner({ isPro = false }) {
    const [isVisible, setIsVisible] = useState(false)
    const [isExtensionInstalled, setIsExtensionInstalled] = useState(false)

    useEffect(() => {
        // Check if user has dismissed the banner
        const dismissed = localStorage.getItem('inkspot_extension_banner_dismissed')
        const dismissedAt = dismissed ? new Date(dismissed) : null
        const now = new Date()

        // Show banner again after 7 days
        const shouldShow = !dismissedAt || (now - dismissedAt) > 7 * 24 * 60 * 60 * 1000

        // Check if extension is installed using Chrome's messaging API
        const EXTENSION_ID = 'bjifgkinjedaikncmccpihmclmehhmoo'

        const checkExtension = async () => {
            try {
                // Check if chrome.runtime is available (Chrome browser)
                if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
                    chrome.runtime.sendMessage(EXTENSION_ID, { type: 'PING' }, (response) => {
                        if (chrome.runtime.lastError || !response) {
                            // Extension not installed
                            setIsExtensionInstalled(false)
                            if (shouldShow) {
                                setIsVisible(true)
                            }
                        } else {
                            // Extension is installed
                            setIsExtensionInstalled(true)
                            setIsVisible(false)
                        }
                    })
                } else {
                    // Not Chrome browser or no runtime API
                    setIsExtensionInstalled(false)
                    if (shouldShow) {
                        setIsVisible(true)
                    }
                }
            } catch {
                // Error checking extension
                setIsExtensionInstalled(false)
                if (shouldShow) {
                    setIsVisible(true)
                }
            }
        }

        checkExtension()
    }, [])

    const handleDismiss = () => {
        localStorage.setItem('inkspot_extension_banner_dismissed', new Date().toISOString())
        setIsVisible(false)
    }

    const handleInstall = () => {
        // Open Chrome Web Store extension page
        window.open('https://chromewebstore.google.com/detail/inkspot-ai-writing-assist/bjifgkinjedaikncmccpihmclmehhmoo', '_blank')
    }

    if (!isVisible || isExtensionInstalled) {
        return null
    }

    return (
        <div className="glass-card p-4 mb-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
            <div className="flex items-start sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
                        <Chrome className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-white mb-1">
                            Install the Chrome Extension
                        </h3>
                        <p className="text-sm text-white/60">
                            {isPro
                                ? 'Detect clichés in Gmail, Google Docs, Twitter, and more — right where you write!'
                                : 'Detect up to 3 clichés per page — upgrade to Pro for unlimited!'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <ExtensionDemoButton variant="secondary" className="text-sm hidden sm:flex" />
                    <button
                        onClick={handleInstall}
                        className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
                    >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Install Extension</span>
                        <span className="sm:hidden">Install</span>
                    </button>
                    <button
                        onClick={handleDismiss}
                        className="p-2 text-white/40 hover:text-white/70 transition-colors"
                        aria-label="Dismiss"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    )
}

